import { useEffect, useRef } from 'react'

import {
  getGuideTourObserversConfig,
  hasGuideTourUserEligibilitySignal,
  resolveGuideTourUserEligibility,
} from '../config'
import { findGuideTourTargetElement } from '../overlay/utils'
import { getGuideTourProgress, isGuideTourSeen } from '../persistence'
import type {
  GuideTourDefinition,
  GuideTourModalVisibleGate,
  GuideTourStep,
} from '../types'
import { useGuideTour } from '../provider/guideTourContext'
import {
  getGuideTourTargetAvailability,
  isGuideTourTargetPresentInDom,
} from '../hooks'
import {
  findNextAvailableGuideTourStepIndex,
  hasAvailableGuideTourSteps,
  resolveGuideTourForRuntime,
} from '../utils/tourRuntime'
import {
  getGuideTourStepRoute,
  isGuideTourStepOnPathname,
  matchGuideTourRoute,
} from './utils'

export type ParsedGuideTourObserver = {
  kind: 'id' | 'class'
  value: string
}

export const parseGuideTourObserverSelector = (
  selector: string
): ParsedGuideTourObserver => {
  if (selector.startsWith('.')) {
    return { kind: 'class', value: selector.slice(1) }
  }

  if (selector.startsWith('#')) {
    return { kind: 'id', value: selector.slice(1) }
  }

  return { kind: 'id', value: selector }
}

const isDialogLikeElementClosed = (element: HTMLElement): boolean => {
  if ('open' in element && (element as { open?: boolean }).open === false) {
    return true
  }

  return false
}

export const isGuideTourDomElementVisible = (element: HTMLElement): boolean => {
  if (!element.isConnected) {
    return false
  }

  if (isDialogLikeElementClosed(element)) {
    return false
  }

  if (element.hidden || element.getAttribute('aria-hidden') === 'true') {
    return false
  }

  const style = window.getComputedStyle(element)

  const opacity = Number.parseFloat(style.opacity)

  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    (style.opacity !== '' && !Number.isNaN(opacity) && opacity === 0)
  ) {
    return false
  }

  return true
}

const isGuideTourManagedElement = (element: HTMLElement): boolean =>
  element.dataset.testid === 'guide-tour-overlay' ||
  element.dataset.testid === 'guide-tour-step-card' ||
  element.closest('[data-testid="guide-tour-overlay"]') !== null ||
  element.closest('[data-testid="guide-tour-step-card"]') !== null

export const collectGuideTourElementsByClass = (
  className: string,
  root: ParentNode = document
): HTMLElement[] => {
  const results: HTMLElement[] = []

  const visit = (node: Node) => {
    if (!(node instanceof HTMLElement) || isGuideTourManagedElement(node)) {
      return
    }

    if (node.classList.contains(className)) {
      results.push(node)
    }

    if (node.shadowRoot) {
      node.shadowRoot.childNodes.forEach(visit)
    }

    node.childNodes.forEach(visit)
  }

  if (root instanceof Document) {
    root.body.childNodes.forEach(visit)
    return results
  }

  root.childNodes.forEach(visit)

  return results
}

const hasConfiguredBackdropInShadow = (host: HTMLElement): boolean => {
  const backdropClass = getGuideTourModalBackdropClass()

  if (!backdropClass || !host.shadowRoot) {
    return false
  }

  return Array.from(
    host.shadowRoot.querySelectorAll(`.${CSS.escape(backdropClass)}`)
  ).some(node => node instanceof HTMLElement && node.isConnected)
}

const hasVisibleSlottedContent = (host: HTMLElement): boolean =>
  Array.from(host.children).some(
    child => child instanceof HTMLElement && isGuideTourDomElementVisible(child)
  )

export const isGuideTourModalHostActive = (host: HTMLElement): boolean => {
  if (!host.isConnected) {
    return false
  }

  // Hexa V2: backdrop in shadow means open even during opacity animation.
  if (hasConfiguredBackdropInShadow(host)) {
    return true
  }

  // Slotted light-DOM children (tabs, forms) indicate an open dialog host.
  if (hasVisibleSlottedContent(host)) {
    return true
  }

  return isGuideTourDomElementVisible(host)
}

const getGuideTourModalHostSelectors = (): readonly string[] =>
  getGuideTourObserversConfig().modalHostSelectors

const getGuideTourModalBackdropClass = (): string =>
  getGuideTourObserversConfig().modalBackdropClass

const shouldDetectFullscreenBlockingOverlay = (): boolean =>
  getGuideTourObserversConfig().detectFullscreenBlockingOverlay

const hasVisibleGuideTourModalBackground = (
  style: CSSStyleDeclaration
): boolean => {
  const { backgroundColor } = style

  return (
    backgroundColor !== '' &&
    backgroundColor !== 'transparent' &&
    backgroundColor !== 'rgba(0, 0, 0, 0)'
  )
}

const isGuideTourGenericModalOverlay = (element: HTMLElement): boolean => {
  if (
    !isGuideTourDomElementVisible(element) ||
    isGuideTourManagedElement(element)
  ) {
    return false
  }

  const style = window.getComputedStyle(element)

  if (style.position !== 'fixed' && style.position !== 'absolute') {
    return false
  }

  if (!hasVisibleGuideTourModalBackground(style)) {
    return false
  }

  const rect = element.getBoundingClientRect()

  return (
    rect.width >= window.innerWidth * 0.85 &&
    rect.height >= window.innerHeight * 0.85
  )
}

export const collectGuideTourGenericModalOverlays = (
  root: ParentNode = document
): HTMLElement[] => {
  const results: HTMLElement[] = []

  const visit = (node: Node) => {
    if (!(node instanceof HTMLElement) || isGuideTourManagedElement(node)) {
      return
    }

    if (isGuideTourGenericModalOverlay(node)) {
      results.push(node)
    }

    if (node.shadowRoot) {
      node.shadowRoot.childNodes.forEach(visit)
    }

    node.childNodes.forEach(visit)
  }

  if (root instanceof Document) {
    root.body.childNodes.forEach(visit)
    return results
  }

  root.childNodes.forEach(visit)

  return results
}

export const collectGuideTourModalHosts = (
  root: ParentNode = document
): HTMLElement[] => {
  const results: HTMLElement[] = []

  for (const selector of getGuideTourModalHostSelectors()) {
    const hosts = root.querySelectorAll(selector)

    for (const host of hosts) {
      if (host instanceof HTMLElement && !isGuideTourManagedElement(host)) {
        results.push(host)
      }
    }
  }

  return results
}

export const getActiveGuideTourModalHosts = (
  root: ParentNode = document
): HTMLElement[] =>
  collectGuideTourModalHosts(root).filter(host =>
    isGuideTourModalHostActive(host)
  )

export const isGuideTourTargetInsideActiveModal = (
  element: HTMLElement,
  root: ParentNode = document
): boolean =>
  getActiveGuideTourModalHosts(root).some(
    host => host === element || host.contains(element)
  )

export const isAnyGuideTourModalActive = (
  root: ParentNode = document
): boolean => {
  if (
    getGuideTourModalBackdropClass() &&
    collectGuideTourElementsByClass(
      getGuideTourModalBackdropClass(),
      root
    ).some(element => isGuideTourDomElementVisible(element))
  ) {
    return true
  }

  if (
    collectGuideTourModalHosts(root).some(host =>
      isGuideTourModalHostActive(host)
    )
  ) {
    return true
  }

  if (!shouldDetectFullscreenBlockingOverlay()) {
    return false
  }

  return collectGuideTourGenericModalOverlays(root).length > 0
}

export const isGuideTourModalGateSatisfied = (
  waitForModalVisible: GuideTourModalVisibleGate,
  root: ParentNode = document
): boolean => {
  if (waitForModalVisible === undefined) {
    return true
  }

  if (waitForModalVisible === false) {
    return true
  }

  if (waitForModalVisible === true) {
    return isAnyGuideTourModalActive(root)
  }

  if (typeof waitForModalVisible !== 'string') {
    return false
  }

  return isGuideTourObserverActive(waitForModalVisible, root)
}

export const isGuideTourObserverActive = (
  selector: string,
  root: ParentNode = document
): boolean => {
  const parsed = parseGuideTourObserverSelector(selector)

  if (parsed.kind === 'class') {
    return collectGuideTourElementsByClass(parsed.value, root).some(element =>
      isGuideTourDomElementVisible(element)
    )
  }

  const element = findGuideTourTargetElement(parsed.value, root)

  if (!element) {
    return false
  }

  return isGuideTourModalHostActive(element)
}

export const areGuideTourObserversHidden = (
  selectors: readonly string[] | undefined,
  root: ParentNode = document
): boolean => {
  if (!selectors?.length) {
    return true
  }

  return !selectors.some(selector => isGuideTourObserverActive(selector, root))
}

export const areGuideTourObserversVisible = (
  selectors: readonly string[] | undefined,
  root: ParentNode = document
): boolean => {
  if (!selectors?.length) {
    return true
  }

  return selectors.every(selector => isGuideTourObserverActive(selector, root))
}

export type GuideTourStepGateOptions = {
  tourWaitForModalHidden?: boolean
  tourWaitForHidden?: readonly string[]
  modalVisibleGate: GuideTourModalVisibleGate
}

/**
 * Resolves whether this step must wait for a modal before showing.
 *
 * - Explicit `waitForModalVisible` on the step wins (`true`, modal id, or `false` to opt out).
 * - After a step with `forceClick`, the next step defaults to waiting for any modal.
 */
export const getGuideTourStepModalVisibleGate = (
  tour: GuideTourDefinition,
  stepIndex: number
): GuideTourModalVisibleGate => {
  const step = tour.steps[stepIndex]

  if (!step) {
    return undefined
  }

  if (step.waitForModalVisible !== undefined) {
    return step.waitForModalVisible
  }

  for (let index = stepIndex - 1; index >= 0; index -= 1) {
    if (tour.steps[index]?.forceClick) {
      return true
    }
  }

  return undefined
}

export const isGuideTourStepInModalLayer = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean => getGuideTourStepModalVisibleGate(tour, stepIndex) !== undefined

export const getGuideTourStepGateOptions = (
  tour: GuideTourDefinition,
  stepIndex: number
): GuideTourStepGateOptions => ({
  ...getGuideTourActivationStepGateOptions(tour, stepIndex),
  modalVisibleGate: getGuideTourStepModalVisibleGate(tour, stepIndex),
})

export const isGuideTourStepGateReady = (
  step: GuideTourStep | null | undefined,
  {
    tourWaitForModalHidden,
    tourWaitForHidden,
    modalVisibleGate,
  }: Partial<GuideTourStepGateOptions> = {}
): boolean => {
  if (!step) {
    return false
  }

  if (tourWaitForModalHidden && isAnyGuideTourModalActive()) {
    return false
  }

  if (!areGuideTourObserversHidden(tourWaitForHidden)) {
    return false
  }

  if (step.waitForModalHidden && isAnyGuideTourModalActive()) {
    return false
  }

  if (!areGuideTourObserversHidden(step.waitForHidden)) {
    return false
  }

  if (!isGuideTourModalGateSatisfied(modalVisibleGate)) {
    return false
  }

  const expectsModal =
    modalVisibleGate !== undefined && modalVisibleGate !== false

  if (!expectsModal && isAnyGuideTourModalActive()) {
    return false
  }

  return true
}

export const shouldWaitForGuideTourStepTarget = (
  modalVisibleGate: GuideTourModalVisibleGate
): boolean => modalVisibleGate === undefined

export const getGuideTourActivationStepGateOptions = (
  tour: GuideTourDefinition,
  stepIndex: number
): GuideTourStepGateOptions => {
  if (stepIndex !== 0) {
    return { modalVisibleGate: undefined }
  }

  return {
    tourWaitForModalHidden: tour.waitForModalHidden,
    tourWaitForHidden: tour.waitForHidden,
    modalVisibleGate: undefined,
  }
}

export const hasGuideTourObserverGates = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean => {
  const step = tour.steps[stepIndex]

  return Boolean(
    tour.waitForModalHidden ||
    tour.waitForHidden?.length ||
    step?.waitForModalHidden ||
    step?.waitForHidden?.length ||
    getGuideTourStepModalVisibleGate(tour, stepIndex) !== undefined
  )
}

export const isGuideTourStepGatesReadyForActivation = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean => {
  const activeTour = resolveGuideTourForRuntime(tour)
  const step = activeTour.steps[stepIndex]
  const gateOptions = getGuideTourActivationStepGateOptions(
    activeTour,
    stepIndex
  )

  return isGuideTourStepGateReady(step, gateOptions)
}

export const isGuideTourStepTargetPresent = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean => {
  const activeTour = resolveGuideTourForRuntime(tour)
  const step = activeTour.steps[stepIndex]

  if (!step) {
    return false
  }

  const targetId = step.targetId
  const modalVisibleGate = getGuideTourStepModalVisibleGate(
    activeTour,
    stepIndex
  )

  if (!targetId) {
    return true
  }

  if (!shouldWaitForGuideTourStepTarget(modalVisibleGate)) {
    return true
  }

  return getGuideTourTargetAvailability(targetId) === 'available'
}

const isGuideTourStepTargetPresentInDom = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean => {
  const activeTour = resolveGuideTourForRuntime(tour)
  const step = activeTour.steps[stepIndex]

  if (!step) {
    return false
  }

  const targetId = step.targetId
  const modalVisibleGate = getGuideTourStepModalVisibleGate(
    activeTour,
    stepIndex
  )

  if (!targetId) {
    return true
  }

  if (!shouldWaitForGuideTourStepTarget(modalVisibleGate)) {
    return true
  }

  return isGuideTourTargetPresentInDom(targetId)
}

/** Gates satisfied and the step target exists in the DOM (or is not required). */
export const isGuideTourActivatable = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean =>
  isGuideTourStepGatesReadyForActivation(tour, stepIndex) &&
  isGuideTourStepTargetPresent(tour, stepIndex)

/**
 * Whether a panel-only tour should appear in header notifications right now.
 *
 * `listInNotifications` tours are listed from any route while unseen. On the
 * tour route they also auto-start like other pending tours once activatable.
 */
export const isGuideTourAnnouncementListed = (
  tour: GuideTourDefinition,
  now: Date = new Date(),
  pathname: string = typeof window !== 'undefined'
    ? window.location.pathname
    : '/'
): boolean => {
  if (!hasAvailableGuideTourSteps(tour, now)) {
    return false
  }

  const activeTour = resolveGuideTourForRuntime(tour)
  const firstStepIndex = findNextAvailableGuideTourStepIndex(activeTour, 0, now)

  if (firstStepIndex === null) {
    return false
  }

  const firstStep = activeTour.steps[firstStepIndex]
  const stepRoute = firstStep
    ? getGuideTourStepRoute(activeTour, firstStep)
    : undefined
  const onTourRoute = stepRoute
    ? matchGuideTourRoute(stepRoute, pathname)
    : true

  if (!onTourRoute) {
    return true
  }

  return (
    isGuideTourStepGatesReadyForActivation(activeTour, firstStepIndex) &&
    isGuideTourStepTargetPresentInDom(activeTour, firstStepIndex)
  )
}

export const GUIDE_TOUR_STEP_ACTIVATION_POLL_MS = 100

export const GUIDE_TOUR_STEP_ACTIVATION_MAX_WAIT_MS = 8_000

export const waitForGuideTourStepActivatable = (
  tour: GuideTourDefinition,
  stepIndex: number,
  {
    pollIntervalMs = GUIDE_TOUR_STEP_ACTIVATION_POLL_MS,
    maxWaitMs = GUIDE_TOUR_STEP_ACTIVATION_MAX_WAIT_MS,
    now = Date.now,
  }: {
    pollIntervalMs?: number
    maxWaitMs?: number
    now?: () => number
  } = {}
): Promise<boolean> => {
  const startedAt = now()

  return new Promise(resolve => {
    const evaluate = () => {
      if (isGuideTourActivatable(tour, stepIndex)) {
        resolve(true)
        return
      }

      if (now() - startedAt >= maxWaitMs) {
        resolve(false)
        return
      }

      window.setTimeout(evaluate, pollIntervalMs)
    }

    evaluate()
  })
}

const GUIDE_TOUR_ACTIVATION_POLL_MS = 250

type UseGuideTourActivationOptions = {
  enabled?: boolean
  stepIndex?: number
  viewedStepCount?: number
  pathname?: string
  isSeen?: typeof isGuideTourSeen
}

export const useGuideTourActivation = (
  tour: GuideTourDefinition | null | undefined,
  {
    enabled = true,
    stepIndex = 0,
    viewedStepCount = 0,
    isSeen = isGuideTourSeen,
    pathname = window.location.pathname,
  }: UseGuideTourActivationOptions = {}
) => {
  const {
    state,
    start,
    isTourDismissedOnPath,
    isRouteTourPaused,
    isRouteTourChainCooldownActive,
  } = useGuideTour()
  const hasAutoStartedRef = useRef(false)

  useEffect(() => {
    hasAutoStartedRef.current = false
  }, [tour?.id, tour?.version, stepIndex])

  useEffect(() => {
    if (!enabled || !tour) {
      return undefined
    }

    if (resolveGuideTourUserEligibility() === 'blocked') {
      return undefined
    }

    if (state.status !== 'idle') {
      return undefined
    }

    if (hasAutoStartedRef.current) {
      return undefined
    }

    const activeTour = resolveGuideTourForRuntime(tour)
    const step = activeTour.steps[stepIndex]
    const targetId = step?.targetId
    const stepGateOptions = getGuideTourStepGateOptions(activeTour, stepIndex)
    const modalVisibleGate = stepGateOptions.modalVisibleGate
    const waitsForStepTarget =
      Boolean(targetId) && shouldWaitForGuideTourStepTarget(modalVisibleGate)
    const shouldPollForObservers =
      hasGuideTourObserverGates(activeTour, stepIndex) ||
      isRouteTourPaused(pathname) ||
      isRouteTourChainCooldownActive() ||
      waitsForStepTarget

    const shouldPollForEligibility = hasGuideTourUserEligibilitySignal()

    const tryStart = (): boolean => {
      const eligibility = resolveGuideTourUserEligibility()

      if (eligibility === 'blocked' || eligibility === 'pending') {
        return false
      }

      if (isSeen(tour.id, tour.version)) {
        return true
      }

      if (isTourDismissedOnPath(tour.id, tour.version, pathname)) {
        return true
      }

      if (isRouteTourPaused(pathname)) {
        return false
      }

      if (isRouteTourChainCooldownActive()) {
        return false
      }

      if (!isGuideTourStepGateReady(step, stepGateOptions)) {
        return false
      }

      if (
        targetId &&
        shouldWaitForGuideTourStepTarget(modalVisibleGate) &&
        isGuideTourStepOnPathname(activeTour, stepIndex, pathname) &&
        !findGuideTourTargetElement(targetId)
      ) {
        return false
      }

      const savedProgress = getGuideTourProgress(tour.id, tour.version)
      const shouldResume =
        savedProgress !== null || viewedStepCount > 0 || stepIndex > 0

      start(tour.id, {
        stepIndex,
        viewedStepCount,
        resume: shouldResume,
      })
      hasAutoStartedRef.current = true
      return true
    }

    let cancelled = false
    let observer: MutationObserver | null = null
    let pollIntervalId: number | undefined

    const stopWatching = () => {
      observer?.disconnect()
      observer = null

      if (pollIntervalId !== undefined) {
        window.clearInterval(pollIntervalId)
        pollIntervalId = undefined
      }
    }

    const attemptStart = () => {
      if (cancelled) {
        return
      }

      if (resolveGuideTourUserEligibility() === 'blocked') {
        stopWatching()
        return
      }

      if (tryStart()) {
        stopWatching()
        return
      }

      if (!observer) {
        observer = new MutationObserver(() => {
          attemptStart()
        })
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'open'],
        })
      }

      if (
        (shouldPollForEligibility || shouldPollForObservers) &&
        pollIntervalId === undefined
      ) {
        pollIntervalId = window.setInterval(() => {
          attemptStart()
        }, GUIDE_TOUR_ACTIVATION_POLL_MS)
      }
    }

    queueMicrotask(attemptStart)

    return () => {
      cancelled = true
      stopWatching()
    }
  }, [
    enabled,
    tour,
    start,
    state.status,
    isSeen,
    stepIndex,
    viewedStepCount,
    pathname,
    isTourDismissedOnPath,
    isRouteTourPaused,
    isRouteTourChainCooldownActive,
  ])
}
